/**
 * CA 路由守卫决策（纯函数）。
 * 由 src/router/index.js 的 beforeEach 调用；此处不含任何副作用（不 commit、不跳转，
 * 只返回「要做什么」的描述），便于用 Node 断言脚本直接验证。
 *
 * 守卫要干两件事，判断依据不同、千万别混：
 *  1. 「要不要整站接管成 CA 门户」—— 只看域名解析结果 hostCode。
 *     绝不能看 store 里的 customerCode：本地开发用 ?code 进过 CA 页后 store 里长期存着
 *     code，若拿它当接管依据，再打开主站 / 会被误跳成 CA 门户，毁掉主站开发体验。
 *  2. 「当前是哪个客户（customerCode）」—— 四级来源：域名 > URL 的 ?code > store 缓存
 *     > 本地开发默认编码（仅 localhost / 私网 IP，见 util/domainMap.resolveDevCustomerCode）。
 *     路由 path 里不带客户编码，刷新 / 站内跳转时 URL 上没有 code，必须靠 store 兜底；
 *     局域网 IP 与 localhost 不同源、localStorage 不互通，首次用 IP 访问时靠开发默认编码兜底。
 * 一句话：store 能决定「是哪个客户」，不能决定「要不要接管」。
 */

/**
 * 需要登录态的 CA 页面（路由 name）。
 * 未登录进入这些页时弹出登录框但不阻断导航（沿用主站既有「弹框不阻断」模式），
 * 后续接口若返回 401/403 由 axios 拦截器兜底再次弹框。
 * 申请页 ca-apply 不在其中：未登录也可浏览，只在点「去结算」时才要求登录。
 */
export const CA_REQUIRE_LOGIN = [
  'ca-orders',
  'ca-order-detail',
  'ca-order-edit',
  'ca-order-renew',
  'ca-wire-transfer',
  'ca-contact',
]

/**
 * @param {object} to 路由目标（取 path / name / query.code）
 * @param {{ hostCode?: string, storeCode?: string, devCode?: string, loggedIn?: boolean }} ctx
 * @returns {{ action: 'pass', commitCode?: string, showLogin?: boolean }
 *         | { action: 'redirect', name: string, commitCode?: string }}
 */
export function decideCaGuard(to, ctx) {
  const { hostCode = '', storeCode = '', devCode = '', loggedIn = false } = ctx || {}

  // ⓪ 兜底错误页自身放行：它就是「识别不出客户」的落地页，再拦截会重定向死循环
  if (to.name === 'ca-error') return { action: 'pass' }

  const isCaRoute = to.path === '/ca' || to.path.indexOf('/ca/') === 0

  // 命中 CA 客户域名 + 访问的不是 CA 页面 → 整站接管到 CA 入口（该域名下主站功能不可达）
  if (hostCode && !isCaRoute) {
    return { action: 'redirect', name: loggedIn ? 'ca-orders' : 'ca-apply' }
  }

  if (isCaRoute) {
    // customerCode 四级解析：域名（生产主通道）> URL ?code（本地调试）> store 会话缓存 > 本地开发默认编码
    const code = hostCode || to.query.code || storeCode || devCode
    if (!code) return { action: 'redirect', name: 'ca-error' }
    const commitCode = code !== storeCode ? code : undefined

    // 根路径 /ca 没有自己的页面：按登录态分流到我的订单 / 申请页
    if (to.path === '/ca') {
      const result = { action: 'redirect', name: loggedIn ? 'ca-orders' : 'ca-apply' }
      if (commitCode) result.commitCode = commitCode
      return result
    }

    const result = { action: 'pass' }
    if (commitCode) result.commitCode = commitCode
    // 未登录访问需登录页 → 打个标记，由守卫弹登录框（不阻断导航）
    if (!loggedIn && CA_REQUIRE_LOGIN.indexOf(to.name) > -1) result.showLogin = true
    return result
  }

  return { action: 'pass' }
}

/**
 * 「回 CA 入口」的跳转目标（当前只有退出登录一处用）：
 *  - 本地 / 联调（域名解析不出客户编码）：目标附 ?code 作双保险 —— 即使 localStorage
 *    被整包清过，守卫也能从 URL 重新解析出客户，退登后一定回得到本客户申请页；
 *  - 生产（域名能解析出客户编码）：不带 code，URL 保持干净。
 */
export function resolveCaEntryTarget(hostCode, storeCode) {
  const isDev = !hostCode
  return isDev && storeCode ? { name: 'ca-apply', query: { code: storeCode } } : { name: 'ca-apply' }
}
